import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormlyForm, FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ScDialogComponent } from '../../models';
import { ScFieldConfig, ScFormField, ScBaseFormField } from '../../models/table.model';

@Component({
  selector: 'sc-generic-form',
  templateUrl: './sc-generic-form.component.html',
  styleUrls: ['./sc-generic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScGenericFormComponent implements OnInit, ScDialogComponent, OnDestroy {
  @ViewChild(FormlyForm, { static: false }) formlyForm: FormlyForm;
  @Input() data;

  @Input() public form: FormGroup | any;
  @Input() public isForFormly: boolean;
  @Input() public options: FormlyFormOptions[];

  public files: string[] = [];
  public urls: string[] = [];
  public _tabs: { label: string; fields: ScFieldConfig[] }[];
  public loading: boolean;

  private _formFields: ScFormField[] = [];
  private _fieldsSubject$: BehaviorSubject<ScFormField[]> = new BehaviorSubject([]);

  private _unsubscribe$: Subject<void> = new Subject();

  constructor(private _cd: ChangeDetectorRef) {}

  @Input() set formFields(formFields: any[]) {
    if (formFields && formFields.length) {
      this._formFields = formFields;
      this._fieldsSubject$.next(this._formFields);
    }
  }

  get formFields() {
    return this._formFields;
  }

  @Input() set tabs(tabs) {
    this._tabs = tabs;
    this.form = new FormArray(this.tabs.map(() => new FormGroup({})));
    this.options = this.tabs.map(() => {}) as any;
  }

  get tabs() {
    return this._tabs;
  }

  ngOnInit() {
    this._fieldsSubject$
      .asObservable()
      .pipe(
        takeUntil(this._unsubscribe$),
        filter(fields => fields && fields.length > 0)
      )
      .subscribe(formFields => {
        this.init(formFields);
      });
  }

  public init(formFields) {
    if (!this.form) {
      this.form = new FormGroup({});
    }
    if (!this.isForFormly) {
      formFields
        .filter((field: ScFormField) => field.type !== 'info')
        .forEach((field: ScBaseFormField) => {
          let validators = [];
          let value = null;
          switch (field.type) {
            case 'datepicker':
              value = this.data[field.field] ? new Date(this.data[field.field]) : null;
              break;
            default:
              value = this.data[field.field];
          }

          if (field.default && !value) {
            value = field.default;
          }

          if (field.required) {
            validators.push(Validators.required);
          }

          if (field.validators) {
            validators = [...validators, ...field.validators];
          }

          const control = this.form.get(field.field);

          if (!control) {
            this.form.addControl(field.field, new FormControl(value, validators));
          } else {
            control.reset(value);
          }
        });
      this._cd.markForCheck();
    }
  }

  public trackByIndex = (index: number): number => index;

  public addFormFields(formFields: ScBaseFormField[]) {
    this.init(formFields);
    const newFields = formFields.filter(s => !this._formFields.some((n: ScBaseFormField) => n.field === s.field));
    this._formFields = [...this._formFields, ...newFields];
  }

  public removeFormFields(formFields: ScBaseFormField[]) {
    formFields.forEach(field => {
      this._formFields = this._formFields.filter((f: ScBaseFormField) => f.field !== field.field);
    });
  }

  public compareFn(itemId: string) {
    return (c1: any, c2: any): boolean => (c1 && c2 ? c1[itemId] === c2[itemId] : c1 === c2);
  }

  public deleteAttachment(field: string) {
    this.form.controls[field].setValue(null);
  }

  public ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    if (this.formlyForm) {
      this.formlyForm.form.reset();
      this.formlyForm.fields = [];
    }
  }
}
